import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { HiMenu, HiX } from 'react-icons/hi';
import { useAuth } from './context/AuthContext';
import { 
  FaHome, 
  FaTshirt, 
  FaWallet, 
  FaChartLine,
  FaGem,
  FaBell,
  FaCalendarAlt,
  FaListAlt,
  FaBook,
  FaStickyNote,
  FaMoneyBillWave,
  FaSignOutAlt,
  FaLock,
  FaCloud,  // Add this import
  FaCalendarCheck,  // Add this import
  FaClock  // Add this import
} from 'react-icons/fa';

function Sidebar({ isOpen, toggleSidebar }) {
  const [isCollapsed, setIsCollapsed] = useState(true); // Add collapsed state
  const location = useLocation();
  const navigate = useNavigate();
  const { logout } = useAuth();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const menuItems = [
    { to: "/dashboard", label: "Dashboard", icon: FaHome },
    { to: "/events", label: "Events", icon: FaCalendarAlt },
    { to: "/tweek", label: "Tweek", icon: FaCalendarCheck },  // Add this line
    { to: "/clothing", label: "Clothing", icon: FaTshirt },
    { to: "/income", label: "Income", icon: FaMoneyBillWave },
    { to: "/expense", label: "Expense", icon: FaWallet },
    { to: "/investment", label: "Investment", icon: FaChartLine },
    { to: "/accessories", label: "Accessories", icon: FaGem },
    { to: "/reminder", label: "Reminders", icon: FaBell },
    { to: "/habits", label: "Habits", icon: FaListAlt },
    { to: "/notes", label: "Notes", icon: FaStickyNote },
    { to: "/diary", label: "Daily Diary", icon: FaBook },
    { to: "/drive", label: "Drive", icon: FaCloud },  // Add this item
    { to: "/routine", label: "Routine", icon: FaClock },  // Add this item
    { to: "/secrets", label: "Secrets", icon: FaLock }
  ];

  return (
    <>
      {/* Overlay */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 backdrop-blur-sm lg:hidden z-30"
          onClick={toggleSidebar}
        ></div>
      )}

      {/* Sidebar */}
      <aside 
        className={`
          fixed top-0 left-0 h-full bg-gradient-to-b from-gray-100 to-white
          shadow-lg transform transition-all duration-300 ease-in-out z-40 border-r border-gray-200
          ${isOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
          ${isCollapsed ? 'w-16' : 'w-64'}
        `}
      >
        <div className="flex flex-col h-full pt-16 lg:pt-0">
          {/* Logo/Brand Section with Toggle */}
          <div className="px-4 py-6 border-b border-gray-200 flex items-center justify-center">
            {!isCollapsed ? (
              <>
                <h2 className="text-lg font-medium text-gray-800 tracking-wide text-center">
                  Life Sync
                </h2>
                <button 
                  onClick={() => setIsCollapsed(true)}
                  className="ml-2 text-gray-500 hover:text-gray-700"
                >
                  <HiX className="w-5 h-5" />
                </button>
              </>
            ) : (
              <button 
                onClick={() => setIsCollapsed(false)}
                className="text-gray-500 hover:text-gray-700"
              >
                <HiMenu className="w-5 h-5" />
              </button>
            )}
          </div>
          
          {/* Navigation Section */}
          <nav className="flex-1 overflow-y-auto py-4">
            <div className="px-2">
              {menuItems.map((item) => {
                const isActive = location.pathname === item.to;
                const Icon = item.icon;
                
                return (
                  <Link
                    key={item.label}
                    to={item.to}
                    className={`
                      flex items-center px-3 py-2 mb-1 rounded-lg text-sm
                      transition-all duration-200 group
                      ${isCollapsed ? 'justify-center' : ''}
                      ${isActive 
                        ? 'bg-blue-500 text-white shadow-sm' 
                        : 'text-gray-600 hover:bg-gray-100'}
                    `}
                    onClick={() => window.innerWidth < 1024 && toggleSidebar()}
                    title={isCollapsed ? item.label : ''}
                  >
                    <Icon className={`w-4 h-4 ${!isCollapsed && 'mr-2'} transition-colors
                      ${isActive ? 'text-white' : 'text-gray-500 group-hover:text-gray-700'}`} 
                    />
                    {!isCollapsed && <span className="font-medium">{item.label}</span>}
                  </Link>
                );
              })}
            </div>
          </nav>

          {/* Footer Section */}
          <div className="p-3 border-t border-gray-200">
            <button
              onClick={handleLogout}
              className={`
                flex items-center px-3 py-2 rounded-lg text-sm
                text-gray-600 hover:bg-red-50 hover:text-red-600 transition-all duration-200
                ${isCollapsed ? 'justify-center w-10 mx-auto' : 'w-full justify-center'}
              `}
              title={isCollapsed ? 'Logout' : ''}
            >
              <FaSignOutAlt className="w-4 h-4" />
              {!isCollapsed && <span className="ml-2 font-medium">Logout</span>}
            </button>
            {!isCollapsed && (
              <div className="mt-3 text-center">
                <p className="text-xs text-gray-400">Version 1.0.0</p>
              </div>
            )}
          </div>
        </div>
      </aside>
    </>
  );
}

export default Sidebar;
